import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, OnDestroy, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import { ClientModifyComponent } from 'app/administration/clients/modify/client-modify.component';
import { buildUnsavedChangesSwalOptions, ClientSettingsPanelService } from 'app/services/client-settings-panel.service';

/**
 * Right-side slide-out panel that hosts the existing client settings form (ClientModifyComponent)
 * for the currently selected client, so admins can edit it without leaving the page (EN-3503).
 */
@Component({
	selector: 'app-client-settings-panel',
	standalone: true,
	imports: [CommonModule, ClientModifyComponent],
	templateUrl: './client-settings-panel.component.html',
	styleUrls: ['./client-settings-panel.component.css'],
})
export class ClientSettingsPanelComponent implements OnDestroy {
	private readonly destroyRef = inject(DestroyRef);

	isOpen = false;
	clientId?: string;

	/** The body's inline `overflow` captured while locking, restored verbatim on unlock. */
	private previousBodyOverflow: string | null = null;

	@ViewChild(ClientModifyComponent) private clientModify?: ClientModifyComponent;

	constructor(private readonly panelService: ClientSettingsPanelService) {
		this.panelService.state$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
			this.isOpen = state.open;
			this.clientId = state.clientId;
			this.toggleBodyScrollLock(state.open);
			if (state.open) {
				// Let the sidebar consult unsaved-changes state before switching clients.
				this.panelService.setDirtyChecker(() => !!this.clientModify && this.clientModify.hasUnsavedChanges);
				this.panelService.setUnsavedChangesSummaryProvider(() => this.clientModify?.unsavedChangesSummary ?? []);
			}
		});
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.isOpen) {
			this.attemptClose();
		}
	}

	/** Closes the panel, prompting to discard or continue editing when there are unsaved changes (AC8). */
	attemptClose(): void {
		if (!this.clientModify || !this.clientModify.hasUnsavedChanges) {
			this.panelService.close();
			return;
		}

		Swal.fire(
			buildUnsavedChangesSwalOptions(
				'You have unsaved changes. Discard them or continue editing?',
				this.clientModify.unsavedChangesSummary
			)
		).then((result) => {
			if (result.isConfirmed) {
				this.panelService.close();
			}
		});
	}

	private toggleBodyScrollLock(lock: boolean): void {
		const body = document.getElementsByTagName('body')[0];
		if (lock) {
			// Capture the existing inline overflow once (the `null` sentinel distinguishes
			// "not locked" from a captured empty string) so we don't clobber styling another
			// overlay/modal may have set.
			if (this.previousBodyOverflow === null) {
				this.previousBodyOverflow = body.style.overflow;
			}
			body.style.overflow = 'hidden';
		} else if (this.previousBodyOverflow !== null) {
			body.style.overflow = this.previousBodyOverflow;
			this.previousBodyOverflow = null;
		}
	}

	ngOnDestroy(): void {
		this.panelService.close();
		this.toggleBodyScrollLock(false);
	}
}
